import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useCMS } from '../context/CMSContext';
import { whyChooseUs as defaultItems } from '../data/mockData';

const FADE_UP = { opacity: 0, y: 20 };
const VISIBLE = { opacity: 1, y: 0 };

const Row = ({ item, index, inView }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    animate={inView ? VISIBLE : {}}
    transition={{ delay: 0.08 * index, duration: 0.6 }}
    className="group grid grid-cols-12 gap-6 py-8 lg:py-10 border-t border-white/8 hover:border-white/20 transition-colors duration-500"
    data-testid={`why-row-${item.id}`}
  >
    <div className="col-span-12 lg:col-span-2">
      <span className="font-display text-3xl lg:text-4xl font-medium text-white/30 group-hover:text-white/60 transition-colors duration-500">
        {item.number}
      </span>
    </div>
    <div className="col-span-12 lg:col-span-5">
      <h3 className="font-display text-2xl lg:text-3xl font-medium tracking-tight text-white leading-snug">
        {item.title}
      </h3>
    </div>
    <div className="col-span-12 lg:col-span-5">
      <p className="text-white/55 text-base lg:text-lg leading-relaxed font-light">
        {item.description}
      </p>
    </div>
  </motion.div>
);

const WhyChooseSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });
  const { get } = useCMS();
  const items = get('why_choose_us') || defaultItems;

  return (
    <section id="why-us" ref={ref} className="relative py-24 lg:py-32 bg-[#050914] border-t border-white/5" data-testid="why-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-8 mb-16 lg:mb-20 lg:items-end">
          <div className="lg:col-span-5">
            <motion.p initial={FADE_UP} animate={inView ? VISIBLE : {}}
              className="inline-flex items-center gap-3 text-xs tracking-[0.25em] uppercase text-white/50 font-bold mb-6">
              <span className="w-8 h-px bg-white/30" />
              Why Choose Us
            </motion.p>
            <motion.h2 initial={FADE_UP} animate={inView ? VISIBLE : {}} transition={{ delay: 0.1 }}
              className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tighter text-white leading-[1.05]">
              Five reasons we&rsquo;re the partner that lasts.
            </motion.h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 lg:pb-3">
            <motion.p initial={FADE_UP} animate={inView ? VISIBLE : {}} transition={{ delay: 0.2 }}
              className="text-white/55 text-base lg:text-lg font-light leading-relaxed">
              Most agencies hand over a website and disappear. We stay close, measure what matters, and keep iterating long after launch &mdash; because that&rsquo;s when the actual business case starts compounding.
            </motion.p>
          </div>
        </div>

        <div>
          {items.map((item, index) => (
            <Row key={item.id} item={item} index={index} inView={inView} />
          ))}
          <div className="border-t border-white/8" />
        </div>
      </div>
    </section>
  );
};

export default WhyChooseSection;
